import animation from '@helpers/animation';
import brand from '@helpers/brand';
import { cssVar, setCssVar } from '@helpers/cssVar';
import fonts from '@helpers/fonts';
import { Device, from, until } from '@helpers/media';
import HeadingStyles from '@stories/Components/Global/Typography/Heading.styles';
import ParagraphStyles from '@stories/Components/Global/Typography/Paragraph.styles';
import SectionWrapper from '@stories/Components/SectionWrapper/SectionWrapper';
import SvgChevron from '@img/icons/chevron-right.svg';
import styled, { css } from 'styled-components';
const linkStyles = css `
  ${setCssVar('bgColor', brand.sage.sageLight)};

  display: inline;
  position: relative;
  border: none;
  border-radius: 1px;
  background: none;
  color: ${cssVar('fgColor')};
  padding: 0;
  position: relative;
  text-decoration: none;
  transition: color 0.3s;
  transition-timing-function: ${animation.secondary};

  &:after {
    content: '';
    display: block;
    position: absolute;
    left: 0;
    bottom: -0.2rem;
    width: 100%;
    height: 1px;
    background-color: ${cssVar('fgColor')};
    transition: transform 0.3s, background-color 0.3s;
    transition-timing-function: ${animation.secondary};
    transform: scaleX(0);
  }

  @media ${from(Device.TabletLarge)} {
    padding: 0;
  }

  &:after {
    transform: scaleX(1);
  }

  &:hover {
    color: ${cssVar('bgColor')};

    &:after {
      transform: scaleX(0.5);
      background-color: ${cssVar('bgColor')};
    }
  }

  &:active {
    color: ${cssVar('fgColor')};
  }

  &:focus {
    color: ${cssVar('bgColor')};
    outline: 2px solid ${cssVar('bgColor')};
    outline-offset: 6px;

    &:after {
      background-color: ${cssVar('bgColor')};
    }
  }
`;
const tableStyles = css `
  margin: 0 0 16px;

  th,
  td {
    border: 1px solid ${cssVar('fgColor')};
    color: ${cssVar('fgColor')};
    ${ParagraphStyles.RegularStyles};
    height: auto !important; // override inline height - no point this being content editable.
    margin: 0;
    padding: 8px 16px;
  }
`;
export const contentStyles = css `
  color: ${cssVar('fgColor')};
  ${fonts.matter.regular};

  h1 {
    ${HeadingStyles.H1Styles};
  }

  h2 {
    ${HeadingStyles.H2Styles};

    @media ${from(Device.TabletLarge)} {
      margin-bottom: 45px;
    }
  }

  h3 {
    ${HeadingStyles.H3Styles};

    @media ${from(Device.TabletLarge)} {
      margin-bottom: 40px;
    }
  }

  h4 {
    ${HeadingStyles.H4Styles};

    @media ${from(Device.TabletLarge)} {
      margin-bottom: 40px;
    }
  }

  h5 {
    ${HeadingStyles.H5Styles};
  }

  p {
    ${ParagraphStyles.LargeStyles};
    line-height: 1.15;

    @media ${from(Device.Tablet)} {
      line-height: 1.15;
    }

    @media ${from(Device.TabletLarge)} {
      margin-bottom: 30px;
      line-height: 1.15;
    }

    @media ${from(Device.DesktopSmall)} {
      line-height: 1.15;
    }

    @media ${from(Device.DesktopLarge)} {
      line-height: 1.15;
    }
  }

  strong {
    ${fonts.matter.medium};
  }

  ol,
  ul {
    display: block;
    ${ParagraphStyles.LargeStyles};
    padding: 0 0 0 30px;

    li {
      margin-bottom: 20px;

      @media ${from(Device.TabletLarge)} {
        margin-bottom: 30px;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  ul {
    li {
      position: relative;

      &:before {
        content: '';
        display: block;
        width: 20px;
        height: 20px;
        background-image: url(${SvgChevron});
        background-repeat: no-repeat;
        position: absolute;
        left: -30px;
        top: 0;

        @media ${from(Device.Tablet)} {
          top: 3px;
        }

        @media ${from(Device.TabletLarge)} {
          top: 6px;
        }
      }
    }
  }

  ol {
    list-style: decimal;

    li {
      display: list-item;
    }
  }

  a {
    ${linkStyles};
  }

  > *:last-child {
    margin-bottom: 0;
  }

  table {
    ${tableStyles};
  }

  hr {
    margin: 16px 0;
  }

  sup {
    position: relative;
    top: -0.5em;
    font-size: 80%;
  }

  sub {
    position: relative;
    top: 0.2em;
    font-size: 80%;
  }

  blockquote {
    ${fonts.matter.medium};
    font-size: 18px;
    line-height: 28px;
    margin: 20px 0;

    @media ${from(Device.TabletLarge)} {
      font-size: 26px;
      line-height: 36px;
    }
  }

  cite {
    ${fonts.matter.regular};
    font-size: 16px;
    line-height: 26px;
    font-style: normal;
    color: ${cssVar('accentColor')};

    @media ${from(Device.TabletLarge)} {
      font-size: 18px;
      line-height: 28px;
    }
  }
`;
const Container = styled(SectionWrapper) `
  ${setCssVar('accentColor', brand.sage.sageMedium)};
  ${setCssVar('bgColor', 'transparent')};
  ${setCssVar('fgColor', brand.black)};
  background-color: ${cssVar('bgColor')};
  overflow: hidden;
  padding-top: 150px;
  padding-bottom: 130px;

  &[data-has-anim='true'] {
    padding-top: 70px;
    padding-bottom: 65px;
    overflow: visible;
  }

  // Animations need to be disabled when rendering in kentico admin view: {isEditMode: true}
  @media ${from(Device.TabletLarge)} {
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 1s ${animation.secondary}, transform 1s ${animation.sharp};
    transition-delay: 0.3s;

    @media (prefers-reduced-motion: reduce) {
      transform: translateY(0);
      transition: opacity 1s ${animation.secondary};
    }

    ${({ isActive }) => isActive &&
    css `
        opacity: 1;
        transform: translateY(0);
      `}
  }

  &[data-bg-color='white'] {
    ${setCssVar('bgColor', brand.white)};
  }

  &[data-bg-color='grey'] {
    ${setCssVar('bgColor', brand.grey.greyLight)};
  }

  &[data-bg-color='toast3'] {
    ${setCssVar('bgColor', brand.toast.toastLight)};
  }

  &[data-bg-color='sunshine3'] {
    ${setCssVar('bgColor', brand.sunshine.sunshineLight)};
  }

  &[data-bg-color='plum3'] {
    ${setCssVar('bgColor', brand.plum.plumLight)};
  }
`;
const ContentBlock = styled.div `
  margin: 0;
  width: 100%;
  position: relative;

  ${({ align }) => align === 'center' &&
    css `
      margin: 0 auto;
    `}
`;
const Base = styled.div `
  margin-top: 60px;

  ${({ align }) => align === 'center' &&
    css `
      text-align: center;
      margin: 60px auto 0;
    `}
`;
const Image = styled.img `
  display: block;
  height: auto;
  object-fit: cover;
  object-position: center;
  width: 100%;

  max-height: 250px;

  @media ${from(Device.Tablet)} {
    max-height: 400px;
  }

  @media ${from(Device.TabletLarge)} {
    max-height: 800px;
  }
`;
const ImageSection = styled.div `
  position: absolute;
  bottom: calc(100% - 20px);

  width: 166px;

  [data-anim-align='left'] & {
    right: 100%;

    @media ${from(Device.DesktopSmall)} {
      right: calc(100% + 30px);
    }

    @media ${from(Device.Desktop)} {
      right: calc(100% + 70px);
    }

    @media ${from(Device.DesktopLarge)} {
      right: calc(100% + 130px);
    }
  }

  [data-anim-align='right'] & {
    left: 100%;

    @media ${from(Device.DesktopSmall)} {
      left: calc(100% + 30px);
    }

    @media ${from(Device.Desktop)} {
      left: calc(100% + 70px);
    }

    @media ${from(Device.DesktopLarge)} {
      left: calc(100% + 130px);
    }
  }

  @media ${until(Device.DesktopSmall)} {
    display: none;
  }
`;
const Content = styled.div `
  ${contentStyles}

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    ${fonts.gazpacho.medium};
  }

  ${({ align }) => align === 'center' &&
    css `
      text-align: center;
      margin: 0 auto;
    `}

  ${({ align }) => align === 'left' &&
    css `
      text-align: left;

      h3 {
        @media ${from(Device.TabletLarge)} {
          width: 125%;
          max-width: 950px;
        }
      }
    `}

  ${({ leadParagraph }) => leadParagraph &&
    css `
      p:first-of-type {
        ${ParagraphStyles.LargeStyles};
      }
    `}
`;
export default {
    Base,
    Container,
    Content,
    ContentBlock,
    contentStyles,
    linkStyles,
    Image,
    ImageSection,
};
